import { createFileRoute, Link } from '@tanstack/react-router';
import { DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE, PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { ActionTileGrid, type ActionTileGridTile } from '@interxfi/design-system/components/action-tile/grid/action-tile-grid';
import ClearingIcon from '@interxfi/design-system/icons/react/icon-clearing';
import ClockIcon from '@interxfi/design-system/icons/react/icon-clock';
import ClusterIcon from '@interxfi/design-system/icons/react/icon-cluster';
import DocIcon from '@interxfi/design-system/icons/react/icon-doc';
import InventoryIcon from '@interxfi/design-system/icons/react/icon-inventory';
import JournalsIcon from '@interxfi/design-system/icons/react/icon-journal';
import OrdersIcon from '@interxfi/design-system/icons/react/icon-orders';
import PieIcon from '@interxfi/design-system/icons/react/icon-pie';
import PortfolioIcon from '@interxfi/design-system/icons/react/icon-portfolio';
import ReportsIcon from '@interxfi/design-system/icons/react/icon-reports';
import SweepsIcon from '@interxfi/design-system/icons/react/icon-sweeps';
import TradesIcon from '@interxfi/design-system/icons/react/icon-trades';
import { DtccVendorIllustration } from '@interxfi/design-system/icons/vendor-illustration/dtcc/dtcc-vendor-illustration';
import { RAGL_MANUAL_ENTRY_BASE_PATH } from '../../../../../../../features/clearing-ledger/manual-entry/ragl-manual-entry-routing';
import { CORPACT_CA_ADMIN_ACTION_BASE_PATH } from '../../../../../../../features/corporate-action/ca-admin/action/ca-admin-action-routing';
import { DTC_ASSET_DAY_BASE_PATH } from '../../../../../../../features/dtcc/dtc-asset-day/dtc-asset-day-routing';
import { DOCGEN_MONTHLY_STATEMENT_JOB_BASE_PATH } from '../../../../../../../features/generate/monthly-statement/job/docgen-monthly-statement-job-routing';
import { DOCGEN_TRADE_CONFIRMATION_JOB_BASE_PATH } from '../../../../../../../features/generate/trade-confirmation/job/docgen-trade-confirm-job-routing';
import { TRIGGER_BASE_PATH } from '../../../../../../../features/meta/trigger/trigger-routing';
import { MUTUAL_FUNDS_DIVIDEND_ACCRUAL_BASE_PATH } from '../../../../../../../features/mutual-funds/dividend-accrual/dividend-accrual-routing';
import { REPORT_BASE_PATH } from '../../../../../../../features/report/report-routing';
import { SWEEP_ORDER_BASE_PATH } from '../../../../../../../features/sweeps/clearing/order/sweep-order-routing';
import { TRADE_BASE_PATH } from '../../../../../../../features/trade/trade-routing';
import { getPageHead } from '../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/')({
  head: () => getPageHead('Operations'),
  component: ClearingOperationsIndex,
});

const routes = [
  {
    illustration: <ClearingIcon aria-hidden />,
    title: 'Core',
    subtitle: 'Records, Balances, Transactions',
    to: '/clearing/operations/stock-record',
  },
  {
    illustration: <InventoryIcon aria-hidden />,
    title: 'Inventory',
    subtitle: 'Orders, Balance, Trading Day, and Sell-Down',
    to: '/clearing/operations/inventory',
  },
  {
    illustration: <JournalsIcon aria-hidden />,
    title: 'Journals',
    subtitle: 'Manual Journal Entries',
    to: RAGL_MANUAL_ENTRY_BASE_PATH,
  },
  {
    illustration: <ReportsIcon aria-hidden />,
    title: 'Reports',
    subtitle: 'Archive and Blotters',
    to: REPORT_BASE_PATH,
  },
  {
    illustration: <TradesIcon aria-hidden />,
    title: 'Trades',
    subtitle: 'UTC Match, UTC FIX Sessions, TRF Reports, TRF FIX Sessions',
    to: TRADE_BASE_PATH,
  },
  {
    illustration: <DtccVendorIllustration />,
    title: 'DTCC',
    tone: 'custom',
    subtitle: 'DTC Assets and Reconciliation',
    to: DTC_ASSET_DAY_BASE_PATH,
  },
  {
    illustration: <OrdersIcon aria-hidden />,
    title: 'OMS',
    subtitle: 'FIX Orders & Sessions',
    to: '/clearing/operations/oms',
  },
  {
    illustration: <DocIcon aria-hidden />,
    title: 'Trade Confirmations',
    subtitle: 'Jobs, Tasks',
    to: DOCGEN_TRADE_CONFIRMATION_JOB_BASE_PATH,
  },
  {
    illustration: <DocIcon aria-hidden />,
    title: 'Monthly Statements',
    subtitle: 'Jobs, Tasks',
    to: DOCGEN_MONTHLY_STATEMENT_JOB_BASE_PATH,
  },
  {
    illustration: <SweepsIcon aria-hidden />,
    title: 'Sweeps',
    subtitle: 'Orders, Participants, Jobs, Tasks',
    to: SWEEP_ORDER_BASE_PATH,
  },
  {
    illustration: <PortfolioIcon aria-hidden />,
    title: 'Correspondent Accounts',
    subtitle: 'Special purpose accounts',
    to: '/clearing/operations/account',
  },
  {
    illustration: <ClockIcon aria-hidden />,
    title: 'Triggers',
    subtitle: 'Programmatic Triggers for Clearing Operations',
    to: TRIGGER_BASE_PATH,
  },
  {
    illustration: <PieIcon aria-hidden />,
    title: 'Corporate Actions',
    subtitle: 'Mergers, Spin-Offs, Splits, Dividends',
    to: CORPACT_CA_ADMIN_ACTION_BASE_PATH,
  },
  {
    illustration: <ClusterIcon aria-hidden />,
    title: 'Mutual Funds',
    subtitle: 'Dividend Accruals, Jobs, Tasks, Position Days',
    to: MUTUAL_FUNDS_DIVIDEND_ACCRUAL_BASE_PATH,
  },
] as const satisfies ActionTileGridTile[];

function ClearingOperationsIndex() {
  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <ActionTileGrid items={routes} LinkElement={Link} />
      </PageLayout.Section>
    </PageLayout>
  );
}
